import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import "@/App.css";

// Pages
import HomePage from "./pages/HomePage";
import CoursesPage from "./pages/CoursesPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import UserDashboard from "./pages/UserDashboard";
import CoursePlayerPage from "./pages/CoursePlayerPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPurchases from "./pages/admin/AdminPurchases";
import AdminCertificates from "./pages/admin/AdminCertificates";
import AboutPage from "./pages/AboutPage";
import ContactUs from "./pages/ContactUs";
import ShippingPolicy from "./pages/ShippingPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import CancellationRefundPolicy from "./pages/CancellationRefundPolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import VerifyCertificatePage from "./pages/VerifyCertificatePage";
import NotFoundPage from "./pages/NotFoundPage";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });
      localStorage.setItem("token", response.data.token);
      setUser(response.data.user);
      toast.success("Welcome back!");
      return response.data.user;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
      throw error;
    }
  };

  const signup = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name,
        email,
        password,
      });
      localStorage.setItem("token", response.data.token);
      setUser(response.data.user);
      toast.success("Account created successfully!");
      return response.data.user;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0f3e3e] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, signup, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<CancellationRefundPolicy />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:id" element={<CourseDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-certificate" element={<VerifyCertificatePage />}/>
          {/* Protected User Routes */}
          <Route
            path="/dashboard"
            element={user && user.role === "user" ? <UserDashboard /> : <Navigate to="/login" />}
          />
          <Route
            path="/course-player/:id"
            element={user && user.role === "user" ? <CoursePlayerPage /> : <Navigate to="/login" />}
          />
          
          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={user && user.role === "admin" ? <AdminDashboard /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/courses"
            element={user && user.role === "admin" ? <AdminCourses /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/users"
            element={user && user.role === "admin" ? <AdminUsers /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/purchases"
            element={user && user.role === "admin" ? <AdminPurchases /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/certificates"
            element={user && user.role === "admin" ? <AdminCertificates /> : <Navigate to="/login" />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
